import { Component, Input, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TopicsService } from '@icullen/api/site';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'icullen-topics',
  templateUrl: './topics.component.html',
  styleUrl: './topics.component.scss'
})
export class TopicsComponent implements OnInit {
  @Input() topics: any[] = [];

  private topicsSub: Subscription;
  followedTopicsIds: string[] = [];

  constructor(
    private topicsService: TopicsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.topicsService.followedTopics$.subscribe(topics => {
      this.followedTopicsIds = topics.map(topic => topic.id);
      this.cdr.markForCheck();
    });
    this.topicsService.getFollowedTopics().subscribe();
  }

  isTopicFollowed(topicId: string): boolean {
    return this.followedTopicsIds.includes(topicId);
  }

  getSortedTopics(): any[] {
    if (!this.topics || !this.followedTopicsIds) {
      return this.topics || [];
    }
    return [...this.topics].sort((a, b) => {
      const aFollowed = this.followedTopicsIds.includes(a.id);
      const bFollowed = this.followedTopicsIds.includes(b.id);
      if (aFollowed && !bFollowed) return -1;
      if (!aFollowed && bFollowed) return 1;
      return a.name.localeCompare(b.name);
    });
  }

  toggleTopicFollowing(topic: any, event: Event): void {
    event.stopPropagation();
    this.cdr.markForCheck();
    const wasFollowed = this.isTopicFollowed(topic.id);
    if (wasFollowed) {
      this.followedTopicsIds = this.followedTopicsIds.filter(id => id !== topic.id);
    } else {
      this.followedTopicsIds = [...this.followedTopicsIds, topic.id];
    }
    this.cdr.markForCheck();
    const apiCall = wasFollowed 
      ? this.topicsService.unfollowTopic(topic.id) 
      : this.topicsService.followTopic(topic.id);
    apiCall.pipe(
        finalize(() => {
          this.cdr.markForCheck();
        })
      ).subscribe({
        next: (result) => {
          if (!result.succeeded) {
            this.revertTopicFollowState(topic.id, wasFollowed);
            console.error(`Failed to ${wasFollowed ? 'unfollow' : 'follow'} topic:`, result.message);
          }
        },
        error: (error) => {
          this.revertTopicFollowState(topic.id, wasFollowed);
          console.error(`Error ${wasFollowed ? 'unfollowing' : 'following'} topic:`, error);
        }
      }
    );
  }

  private revertTopicFollowState(topicId: string, wasFollowed: boolean): void {
    if (wasFollowed) {
      if (!this.followedTopicsIds.includes(topicId)) {
        this.followedTopicsIds = [...this.followedTopicsIds, topicId];
      }
    } else {
      this.followedTopicsIds = this.followedTopicsIds.filter(id => id !== topicId);
    }
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.topicsSub) {
      this.topicsSub.unsubscribe();
    }
  }
}